import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, UserPlus, Sparkles, Loader2, Users, Check, Clock, MessageCircle } from "lucide-react";
import Avatar from "@/components/Avatar";

const ACTIVITY_BG = {
  "Sports": "https://images.unsplash.com/photo-1772517403292-b68a77fbc342?auto=format&fit=crop&w=400&q=70",
  "Indoor Sports": "https://images.unsplash.com/photo-1772517403292-b68a77fbc342?auto=format&fit=crop&w=400&q=70",
  "Billiard Games": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=400&q=70",
  "Board Games": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=400&q=70",
  "Video Games": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=400&q=70",
  "Dining": "https://images.pexels.com/photos/25409638/pexels-photo-25409638.jpeg?auto=compress&cs=tinysrgb&w=400",
  "Bars": "https://images.pexels.com/photos/25409638/pexels-photo-25409638.jpeg?auto=compress&cs=tinysrgb&w=400",
  "Clubs": "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=400",
  "Beach": "https://images.unsplash.com/photo-1722410141874-5494d14deeca?auto=format&fit=crop&w=400&q=70",
  "Concerts": "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=400",
  "Art & Crafts": "https://images.pexels.com/photos/36765297/pexels-photo-36765297.jpeg?auto=compress&cs=tinysrgb&w=400",
};

export default function Discover() {
  const { t } = useLang();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [radius, setRadius] = useState(5);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUser, setInviteUser] = useState(null);
  const [groups, setGroups] = useState([]);

  const load = async (r = radius) => {
    setLoading(true);
    try {
      const res = await api.get(`/discover?radius_km=${r}`);
      setMatches(res.data);
    } catch (e) {
      toast.error(t("networkErr"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openInvite = async (u) => {
    setInviteUser(u);
    setInviteOpen(true);
    try {
      const res = await api.get("/groups");
      setGroups(res.data);
    } catch {
      setGroups([]);
    }
  };

  const sendInvite = async (group_id) => {
    try {
      await api.post("/invites", { group_id, invitee_id: inviteUser.id });
      toast.success(t("invitedSuccess"));
      setInviteOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    }
  };

  const connectWith = async (m) => {
    try {
      const res = await api.post("/connections/request", { to_id: m.id });
      const newStatus = res.data.status === "accepted" ? "accepted" : "pending_outgoing";
      setMatches((arr) => arr.map((x) => (x.id === m.id ? { ...x, connection_status: newStatus, connection_id: res.data.id } : x)));
      toast.success(newStatus === "accepted" ? t("nowConnected") : t("connectRequestSent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    }
  };

  const acceptConnect = async (m) => {
    if (!m.connection_id) return;
    try {
      await api.post(`/connections/${m.connection_id}/respond`, { accept: true });
      setMatches((arr) => arr.map((x) => (x.id === m.id ? { ...x, connection_status: "accepted" } : x)));
      toast.success(t("nowConnected"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="discover-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">{t("discover")}</div>
          <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-semibold tracking-tight">{t("yourMatches")}</h1>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{t("matchSubtitle")}</p>
        </div>
        <div className="bg-white border border-[hsl(var(--border))] rounded-2xl p-4 sm:min-w-[260px]">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{t("radius")}</span>
            <span className="text-[hsl(var(--primary))] font-semibold" data-testid="radius-value">{radius} {t("km")}</span>
          </div>
          <Slider
            className="mt-3"
            value={[radius]} min={1} max={25} step={1}
            onValueChange={(v) => setRadius(v[0])}
            onValueCommit={(v) => load(v[0])}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : matches.length === 0 ? (
        <div className="bg-white border border-dashed border-[hsl(var(--border))] rounded-3xl p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="discover-empty">
          <Sparkles className="h-8 w-8 mx-auto text-[hsl(var(--primary))] mb-3" />
          {t("discoverEmpty")}
        </div>
      ) : (
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl overflow-hidden divide-y divide-[hsl(var(--border))]">
          {matches.map((m, idx) => (
            <div
              key={m.id}
              className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5 hover:bg-[hsl(var(--elevated))] transition-colors fade-up"
              data-testid={`match-card-${idx}`}
              style={{ animationDelay: `${idx * 30}ms` }}
            >
              {/* Avatar */}
              <Avatar user={m} size={48} showVerified />

              {/* Body */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium truncate">{m.alias}</span>
                </div>
                <div className="mt-0.5 flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))] flex-wrap">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-[hsl(var(--primary))]" />
                    {m.distance_km != null ? `${m.distance_km.toFixed(1)} ${t("km")}` : (m.city || "")}
                  </span>
                  {m.common_languages?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3 text-[hsl(var(--primary))]" />
                      {m.common_languages.slice(0, 3).join(", ")}
                    </span>
                  )}
                </div>
                {m.common_activities?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.common_activities.slice(0, 4).map((a) => (
                      <span key={a} className="text-[11px] px-2 py-0.5 rounded-full bg-[hsl(var(--elevated))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))]">
                        {a}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0 self-stretch sm:self-auto">
                {m.connection_status === "accepted" ? (
                  <Button
                    onClick={() => nav(`/messages/${m.id}`)}
                    data-testid={`message-btn-${idx}`}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white"
                  >
                    <MessageCircle className="h-4 w-4 me-1.5" />
                    {t("message")}
                  </Button>
                ) : m.connection_status === "pending_outgoing" ? (
                  <Button
                    disabled
                    data-testid={`pending-btn-${idx}`}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] cursor-default"
                  >
                    <Clock className="h-4 w-4 me-1.5" />
                    {t("requested")}
                  </Button>
                ) : m.connection_status === "pending_incoming" ? (
                  <Button
                    onClick={() => acceptConnect(m)}
                    data-testid={`accept-connect-btn-${idx}`}
                    size="sm"
                    className="rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/90 text-white"
                  >
                    <Check className="h-4 w-4 me-1.5" />
                    {t("accept")}
                  </Button>
                ) : (
                  <Button
                    onClick={() => connectWith(m)}
                    data-testid={`connect-btn-${idx}`}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white"
                  >
                    <UserPlus className="h-4 w-4 me-1.5" />
                    {t("connect")}
                  </Button>
                )}
                <Button
                  onClick={() => openInvite(m)}
                  data-testid={`invite-btn-${idx}`}
                  size="sm"
                  className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white"
                >
                  <Users className="h-4 w-4 me-1.5" />
                  {t("inviteToGroup")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-3xl" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>{t("inviteToGroup")}</DialogTitle>
            <DialogDescription>{inviteUser?.alias}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {groups.length === 0 && (
              <div className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">
                {t("noGroups")}{" "}
                <button className="text-[hsl(var(--primary))] font-medium hover:underline" onClick={() => { setInviteOpen(false); nav("/groups"); }}>
                  {t("createGroup")}
                </button>
              </div>
            )}
            {groups.map((g) => {
              const full = g.member_ids.length >= g.max_members;
              return (
                <button
                  key={g.id}
                  disabled={full}
                  onClick={() => sendInvite(g.id)}
                  data-testid={`invite-group-${g.id}`}
                  className="w-full text-start p-3 rounded-2xl border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--elevated))] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{g.name}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{g.member_ids.length}/{g.max_members}</span>
                  </div>
                  {g.activity && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{g.activity}</div>}
                  {full && <div className="text-xs text-[hsl(var(--destructive))] mt-1">{t("groupFull")}</div>}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
